import React, { useState, useEffect } from 'react';
import { coachingAnalytics } from '../../api';
import './TeamHours.css';

const REFRESH_MS = 5 * 60 * 1000;

// "YYYY-MM-DD" -> a Date whose UTC parts are that calendar day, so the viewer's timezone never shifts it
const dateOf = (key) => { const [y, m, d] = key.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); };
const fmt = (key, opts) => dateOf(key).toLocaleDateString([], { ...opts, timeZone: 'UTC' });
const range = (from, to) => {
  const a = dateOf(from);
  const b = dateOf(to);
  const sameMonth = a.getUTCMonth() === b.getUTCMonth() && a.getUTCFullYear() === b.getUTCFullYear();
  return sameMonth
    ? `${a.getUTCDate()} - ${fmt(to, { day: 'numeric', month: 'short' })}`
    : `${fmt(from, { day: 'numeric', month: 'short' })} - ${fmt(to, { day: 'numeric', month: 'short', year: b.getUTCFullYear() !== a.getUTCFullYear() ? '2-digit' : undefined })}`;
};
const h = (n) => `${Math.round(n * 10) / 10}`;

// Head coach: hours for the periods that matter right now, then every week on one list.
const TeamSummary = ({ tick = 0 }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = (fresh) => coachingAnalytics.getSummary(fresh)
      .then(res => { if (!cancelled) { setData(res.data); setError(false); } })
      .catch(() => { if (!cancelled) setError(true); });
    load(tick > 0);
    const timer = setInterval(() => { if (!document.hidden) load(false); }, REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [tick]);

  if (error && !data) return <div className="th-card"><p className="th-error">Could not load the hour totals.</p></div>;
  if (!data) return <div className="th-card"><div className="th-empty">Loading hour totals...</div></div>;

  const { periods, weeks, coaches } = data;
  const maxWeek = Math.max(...weeks.map(w => w.hours), 0.0001);

  return (
    <>
      <div className="th-card">
        <h3 className="th-h">Hours at a glance</h3>
        <p className="th-sub">Tap a box to see it by coach. "To come" is lessons already in the calendar that haven't happened yet.</p>
        <div className="th-glance">
          {periods.map(p => {
            const isOpen = open === p.key;
            const future = p.hoursUpcoming > 0;
            return (
              <button key={p.key} type="button" className={`th-period${isOpen ? ' open' : ''}`} aria-expanded={isOpen} onClick={() => setOpen(isOpen ? '' : p.key)}>
                <span className="th-period-label">{p.label}</span>
                <span className="th-period-range">{range(p.from, p.to)}</span>
                <b className="th-period-hours">{h(p.hours)}<small>h</small></b>
                <span className="th-period-meta">{p.lessons} {p.lessons === 1 ? 'lesson' : 'lessons'}</span>
                {future && <span className="th-period-meta">{h(p.hoursDone)}h done · {h(p.hoursUpcoming)}h to come</span>}
                {isOpen && (
                  <span className="th-period-coaches">
                    {p.perCoach.map(c => (
                      <span key={c._id}><span>{c.name}</span><span>{h(c.hours)}h · {c.lessons}</span></span>
                    ))}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="th-card">
        <h3 className="th-h">All weeks</h3>
        <p className="th-sub">Monday to Sunday. The current week is highlighted; weeks ahead show what is already booked.</p>
        <div className="th-scroll">
          <table className="th-table th-weeks">
            <thead>
              <tr>
                <th>Week</th>
                <th>Hours</th>
                <th>Lessons</th>
                {coaches.map(c => <th key={c._id}>{c.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {weeks.map(w => (
                <tr key={w.start} className={`${w.isCurrent ? 'th-current' : ''}${w.isFuture ? ' th-ahead' : ''}`}>
                  <td>
                    {range(w.start, w.end)}
                    {w.isCurrent && <span className="th-tag">now</span>}
                  </td>
                  <td>
                    <span className="th-wbar"><i style={{ width: `${w.hours === 0 ? 0 : Math.max(3, (w.hours / maxWeek) * 100)}%` }} /></span>
                    <strong>{h(w.hours)}</strong>
                  </td>
                  <td>{w.lessons}</td>
                  {w.perCoach.map(c => <td key={c._id}>{c.hours ? h(c.hours) : '-'}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default TeamSummary;
